import { LitElement, html, css, nothing, unsafeCSS } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { when } from "lit/directives/when.js";
import { classMap } from "lit/directives/class-map.js";
import { resolveColor } from "../helper/utils/color-resolver";
import { cssOnFillEnhance } from "../helper/utils/contrast-color";
import { FormControlBase, FormState } from "../helper/internals/form-control-base";
import { PresetController } from "../helper/controllers/preset-controller";
import { skyFocusRingHostStyles } from "../helper/styles/focus-ring";

export type SkyRadioSelectDetail = { value: string };
export type SkyRadioValidationErrorDetail = { errors: string[] };
export type SkyRadioValueClearedDetail = { value: "" };

/**
 * @element sky-radio
 *
 * @summary Single radio option for grouped or standalone selection.
 *
 * @status stable
 * @since 1.0.0
 *
 * @documentation https://sky-ui.com/components/radio
 *
 * @slot - Content to display instead of the label property.
 *
 * @csspart wrapper - The circular radio control element.
 * @csspart input - The native hidden radio input element.
 * @csspart label - The text label element.
 *
 * @property {string} label - Text label displayed beside the control.
 * @property {string} value - Option value used for selection and form value.
 * @property {boolean} checked - Whether this option is selected.
 * @property {string} name - Radio name used for same-name exclusivity.
 * @property {string} color - Accent color used in checked state.
 * @property {boolean} disabled - Disables user interaction.
 * @property {boolean} readonly - Prevents selection changes while still allowing focus.
 * @property {boolean} required - Enables required validation for standalone usage.
 * @property {string} requiredMessage - Error message for required validation failures.
 * @property {boolean} compact - Compact control size.
 * @property {string} preset - Named prop preset from nearest `sky-config-provider`. Default: `""`.
 *
 * @fires {CustomEvent<SkyRadioSelectDetail>} radio-select - Fired when the option becomes selected.
 * @fires {Event} change - Native change event for form compatibility.
 * @fires {Event} input - Native input event for form compatibility.
 * @fires {CustomEvent<SkyRadioValidationErrorDetail>} validation-error - Fired when validation errors occur.
 * @fires {CustomEvent<SkyRadioValueClearedDetail>} value-cleared - Fired when selection is reset.
 * @method check Selects this radio option.
 * @method focusRadio Focuses the underlying native radio control.
 *
 * @Behavior
 * - Uses a native hidden radio input for semantics and keyboard interaction.
 * - Supports grouped usage inside `sky-radio-group` and standalone usage.
 * - Synchronizes checked state and form value through `FormControlBase`.
 *
 * @example
 * ```html
 * <sky-radio-group label="Deployment" name="deployment">
 *   <sky-radio value="blue" checked color="primary">Blue Environment</sky-radio>
 *   <sky-radio value="green">Green Environment</sky-radio>
 * </sky-radio-group>
 * ```
 * ```vue
 * <template>
 *   <sky-radio-group label="Deployment" name="deployment">
 *     <sky-radio value="blue" checked color="primary">Blue Environment</sky-radio>
 *     <sky-radio value="green">Green Environment</sky-radio>
 *   </sky-radio-group>
 * </template>
 * ```
 * ```jsx
 * export default function Demo() {
 *   return (
 *     <sky-radio-group label="Deployment" name="deployment">
 *       <sky-radio value="blue" checked color="primary">Blue Environment</sky-radio>
 *       <sky-radio value="green">Green Environment</sky-radio>
 *     </sky-radio-group>
 *   );
 * }
 * ```
 */
@customElement("sky-radio")
export class SkyRadio extends FormControlBase {
  @property({ type: String }) label: string = "";
  @property({ type: String }) value: string = "";
  @property({ type: Boolean, reflect: true }) checked: boolean = false;
  @property({ type: String }) color: string = "primary";
  @property({ type: Boolean, reflect: true }) compact = false;
  @state() private _hasMeaningfulSlot = false;

  private syncStyleVars(): void {
    this.style.setProperty("--radio-checked-color", resolveColor(this.color));
  }

  /** After PresetController hostUpdate — paint with current color. */
  protected override update(changed: Map<string, unknown>): void {
    if (changed.size === 0 || changed.has("color") || changed.has("preset")) {
      this.syncStyleVars();
    }
    super.update(changed);
  }

  public check(): void {
    if (this.disabled || this.readonly) return;
    this.checked = true;
    this.syncFormValue();
  }

  public focusRadio(): void {
    const input = this.shadowRoot?.querySelector('input[type="radio"]') as HTMLInputElement | null;
    input?.focus();
  }


  /** Named prop preset from nearest `sky-config-provider`. */
  @property({ type: String, reflect: true }) preset = "";

  private _presets = new PresetController(this);

  static override styles = [
    skyFocusRingHostStyles,
    css`
    :host {
      display: inline-flex;
      align-items: center;
      --sky-radio-size: 18px;
      --sky-radio-dot: 10px;
      --sky-focus-ring-color: var(--radio-checked-color, var(--sky-active-primary));
    }

    :host([compact]) {
      --sky-radio-size: 14px;
      --sky-radio-dot: 8px;
    }

    :host([compact]) label {
      font-size: var(--sky-font-size-xs);
      gap: var(--sky-space-1, 4px);
    }

    /* readonly: allow focus + keyboard traversal, but show subtle visual state */
    :host([readonly]) label {
      cursor: default;
    }

    label {
      display: flex;
      align-items: center;
      gap: var(--sky-space-1\\.5, 6px);
      color: var(--sky-text-primary);
      cursor: pointer;
      font-size: var(--sky-font-size-sm);
      font-family: var(--sky-font);
    }

    label[disabled] {
      pointer-events: none;
      opacity: 0.6;
      cursor: not-allowed;
    }

    :host([disabled]) label,
    :host([disabled]) .radio-wrapper {
      pointer-events: none;
      cursor: not-allowed;
      opacity: 0.6;
    }

    .radio-wrapper {
      position: relative;
      box-sizing: border-box;
      width: var(--sky-radio-size);
      height: var(--sky-radio-size);
      display: inline-block;
      background: var(--sky-glass-solid);
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border: 2px solid var(--sky-text-tertiary);
      border-radius: 50%;
      transition:
        border-color var(--sky-transition-normal, 200ms ease),
        background-color var(--sky-transition-normal, 200ms ease),
        box-shadow var(--sky-transition-normal, 200ms ease);
      cursor: pointer;
      flex-shrink: 0;
    }

    .radio-wrapper::before {
      content: "";
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%) scale(0);
      width: var(--sky-radio-dot);
      height: var(--sky-radio-dot);
      background-color: var(--sky-glass-solid);
      border-radius: 50%;
      transition:
        transform var(--sky-transition-normal, 200ms ease),
        background-color var(--sky-transition-normal, 200ms ease);
    }

    .radio-wrapper.checked {
      border-color: var(--radio-checked-color);
      background-color: var(--radio-checked-color);
    }

    .radio-wrapper.checked::before {
      transform: translate(-50%, -50%) scale(1);
      background-color: var(--sky-on-color-fallback-light, var(--sky-text-white));
    }

    input[type="radio"] {
      position: absolute;
      opacity: 0;
      pointer-events: none;
      width: 0;
      height: 0;
    }

    :host(:focus-within) .radio-wrapper {
      box-shadow: var(--sky-focus-ring);
      border-color: var(--radio-checked-color);
    }

    sky-icon {
      display: inline-block;
      width: 1em;
      height: 1em;
    }

    @media (prefers-reduced-motion: reduce) {
      .radio-wrapper,
      .radio-wrapper::before {
        transition: none;
      }
    }

    ${unsafeCSS(
      cssOnFillEnhance([
        {
          selector: ".radio-wrapper.checked::before",
          fillVar: "--radio-checked-color",
          property: "background-color",
        },
      ]),
    )}
  `,
  ];

  constructor() {
    super();
    this.captureDefaultValue("");
  }
  static override shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };


  override connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener("keydown", this.handleKeydown);
    if (!this.hasAttribute("tabindex")) {
      this.tabIndex = this.disabled ? -1 : 0;
    }
  }

  override disconnectedCallback(): void {
    this.removeEventListener("keydown", this.handleKeydown);
    super.disconnectedCallback();
  }

  override updated(changed: Map<string, unknown>): void {
    const input = this.shadowRoot?.querySelector<HTMLInputElement>(
      "input[type='radio']",
    );

    if (changed.has("checked")) {
      if (input && input.checked !== this.checked) input.checked = this.checked;

      if (changed.get("checked") !== undefined) {
        this.syncFormValue();
        this.syncValidity();
      }
    }
    if (changed.has("disabled") || changed.has("readonly")) {
      if (input) {
        input.disabled = this.disabled;
        input.readOnly = this.readonly;
      }
      this.tabIndex = this.disabled ? -1 : 0;
    }

    if (changed.has("value")) {
      this.syncFormValue();
      this.syncValidity();
    }
  }

  private _slotHasMeaningfulContent(slotEl: HTMLSlotElement): boolean {
    const nodes = slotEl.assignedNodes({ flatten: true });
    return nodes.some((n) =>
      n.nodeType === Node.ELEMENT_NODE
        ? true
        : (n.textContent ?? "").trim().length > 0
    );
  }

  private _onSlotChange = (e: Event) => {
    const slot = e.target as HTMLSlotElement;
    const next = this._slotHasMeaningfulContent(slot);
    if (next !== this._hasMeaningfulSlot) this._hasMeaningfulSlot = next;
  };

  private onNativeChange = (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;

    // Ignore native toggles when disabled or readonly; re-sync checked state.
    if (this.disabled || this.readonly) {
      if (input.checked !== this.checked) {
        input.checked = this.checked;
      }
      return;
    }

    const becameChecked = input.checked && !this.checked;
    this.checked = input.checked;

    // When used individually, uncheck siblings with the same name
    if (this.checked && this.name && !this.closest("sky-radio-group")) {
      const scope: ParentNode = this.form ?? (this.getRootNode() as Document | ShadowRoot);
      const all = Array.from(scope.querySelectorAll<SkyRadio>("sky-radio"));
      for (const r of all) {
        if (r !== this && r.name === this.name) r.checked = false;
      }
    }

    if (becameChecked) {
      this.dispatchEvent(
        new CustomEvent<SkyRadioSelectDetail>("radio-select", {
          detail: { value: this.value },
          bubbles: true,
          composed: true,
        })
      );

      this.reemitNativeEvent("change", e);
      this.reemitNativeEvent("input", e);
      this.syncFormValue();
      this.syncValidity();
    }
  };

  private handleKeydown = (event: KeyboardEvent) => {
    if (this.disabled || this.readonly) return;

    if (event.key === " " || event.key === "Enter") {
      event.preventDefault();
      const input = this.shadowRoot?.querySelector<HTMLInputElement>(
        "input[type='radio']"
      );
      input?.click();
    }
  };

  /** Get the current value for form submission */
  protected override getFormValue(): FormState {
    return this.checked ? this.value : null;
  }

  /** Set value from form state (e.g., form reset) */
  protected override setValueFromFormState(state: FormState): void {
    if (typeof state === "string") {
      this.checked = state === this.value;
    } else {
      this.checked = false;
    }
  }

  /** Get the validity anchor (the hidden input) */
  protected override getValidityAnchor(): HTMLElement | undefined {
    return this.getNativeControl() || undefined;
  }

  /** Check if empty (for required validation) */
  protected override isEmpty(): boolean {
    return !this.checked;
  }

  /** Get the native control (hidden radio input) */
  protected override getNativeControl(): HTMLElement | null {
    return this.shadowRoot?.querySelector<HTMLElement>("input[type='radio']") || null;
  }

  /** Handle native value mutation */
  protected override onNativeValueMutated(_native: any): void {
    const input = this.getNativeControl() as HTMLInputElement;
    if (input) {
      this.checked = input.checked;
    }
  }

  /** Override to provide custom error messages */
  protected override getCustomErrorMessage(): string {
    return this.validationErrors?.[0] || "";
  }

  /** Get current validation errors */
  get validationErrors(): string[] {
    return this.required && !this.checked ? [this.requiredMessage] : [];
  }

  override render() {
    return html`
      <label
        part="label"
        ?disabled=${this.disabled}
        ?readonly=${this.readonly}
        aria-disabled=${this.disabled ? "true" : "false"}
        aria-readonly=${this.readonly ? "true" : "false"}
      >
        <input
          type="radio"
          part="input"
          .name=${this.name}
          .value=${this.value}
          .checked=${this.checked}
          @change=${this.onNativeChange}
          ?disabled=${this.disabled}
          ?required=${this.required}
        />
        <div
          class=${classMap({ "radio-wrapper": true, checked: this.checked })}
          part="wrapper"
        ></div>
        <slot @slotchange=${this._onSlotChange}></slot>
        ${when(!this._hasMeaningfulSlot, () => this.label)}
      </label>
    `;
  }
}
